import React, { useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Award, Users, Film } from "lucide-react";
import { InnerNav } from "../components/InnerNav";
import { Reveal, Overline } from "../components/motion";
import { Discussion } from "../components/Discussion";
import { useTranslate } from "../context/TranslateContext";
import { getPerson, moviesForPerson, songsForPerson, getMovie } from "../mock/data";

export default function PersonDetail() {
  const { id } = useParams();
  const { t } = useTranslate();
  const p = getPerson(id);
  useEffect(() => { window.scrollTo(0, 0); }, [id]);

  if (!p) return (<div><InnerNav /><div className="p-20 text-center font-body text-ash">{t("Person not found.")}</div></div>);

  const films = moviesForPerson(id);
  const songs = songsForPerson(id);
  const listenText = `${p.name}. ${p.bio}`;

  return (
    <div className="min-h-screen bg-gradient-to-b from-paper to-surface">
      <InnerNav />
      <section className="mx-auto grid max-w-[1400px] grid-cols-1 gap-12 px-6 py-14 md:px-12 md:py-20 lg:grid-cols-12">
        <Reveal className="lg:col-span-4">
          <div className="overflow-hidden rounded-3xl border border-edge">
            <img src={p.image} alt={p.name} data-testid="person-image" className="aspect-[4/5] w-full object-cover" />
          </div>
          <div className="mt-6 space-y-2 font-body text-sm text-ash">
            <div><span className="text-gold">{t("Born")}:</span> {p.born}</div>
            <div><span className="text-gold">{t("Languages")}:</span> {p.langs.map((l) => t(l)).join(", ")}</div>
          </div>
        </Reveal>

        <div className="lg:col-span-8">
          <Overline>{t(p.role)}</Overline>
          <h1 data-testid="person-name" className="mt-3 font-display text-5xl font-semibold leading-none tracking-tight text-cream md:text-7xl">{p.name}</h1>
          <p data-testid="person-bio" className="mt-6 max-w-2xl font-body text-lg leading-relaxed text-ash">{t(p.bio)}</p>
          <div className="mt-6"></div>

          {p.awards.length > 0 && (
            <div className="mt-8 flex flex-wrap gap-2" data-testid="person-awards">
              {p.awards.map((a) => (
                <span key={a} className="inline-flex items-center gap-1.5 rounded-full border border-gold/30 bg-gold/10 px-3 py-1.5 font-body text-xs text-gold">
                  <Award className="h-3.5 w-3.5" /> {t(a)}
                </span>
              ))}
            </div>
          )}
        </div>
      </section>

      <div className="mx-auto max-w-[1400px] space-y-14 px-6 pb-28 md:px-12">
        {films.length > 0 && (
          <div>
            <div className="mb-6 flex items-center gap-3">
              <Film className="h-5 w-5 text-gold" />
              <h2 className="font-display text-3xl font-semibold text-cream md:text-4xl">{t("Filmography")}</h2>
            </div>
            <div className="grid grid-cols-2 gap-5 sm:grid-cols-3 lg:grid-cols-4">
              {films.map((m) => (
                <Link key={m.id} to={`/movie/${m.id}`} data-testid={`filmography-${m.id}`}>
                  <motion.div whileHover={{ y: -6 }} className="group overflow-hidden rounded-xl border border-edge">
                    <div className="aspect-[2/3] overflow-hidden">
                      <img src={m.image} alt={m.title} className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" />
                    </div>
                    <div className="p-3">
                      <div className="font-display text-lg text-cream">{m.title}</div>
                      <div className="font-body text-xs text-ash">{m.language} · {m.year}</div>
                    </div>
                  </motion.div>
                </Link>
              ))}
            </div>
          </div>
        )}

        {songs.length > 0 && (
          <div>
            <h2 className="mb-6 font-display text-3xl font-semibold text-cream md:text-4xl">{t("Discography")}</h2>
            <div className="rounded-2xl border border-edge bg-surface/40">
              {songs.map((s) => { const mv = getMovie(s.movie); return (
                <div key={s.id} className="flex items-center gap-4 border-b border-edge px-5 py-4 last:border-0">
                  <div className="min-w-0 flex-1">
                    <div className="font-body text-cream">{s.title}</div>
                    <div className="font-body text-sm text-ash">{mv ? mv.title : t("Independent")} · {t(s.genre)}</div>
                  </div>
                  <span className="font-body text-sm text-ash">{s.duration}</span>
                </div>
              ); })}
            </div>
          </div>
        )}

        {p.collaborators.length > 0 && (
          <div>
            <div className="mb-6 flex items-center gap-3">
              <Users className="h-5 w-5 text-gold" />
              <h2 className="font-display text-3xl font-semibold text-cream md:text-4xl">{t("Frequent Collaborators")}</h2>
            </div>
            <div className="flex flex-wrap gap-5" data-testid="collaborators">
              {p.collaborators.map((cid) => { const c = getPerson(cid); if (!c) return null; return (
                <Link key={cid} to={`/person/${cid}`} className="group flex items-center gap-3 rounded-full border border-edge bg-surface/50 p-1.5 pr-5 transition-colors hover:border-gold">
                  <img src={c.image} alt={c.name} className="h-11 w-11 rounded-full object-cover" />
                  <span>
                    <span className="block font-body text-sm text-cream group-hover:text-gold">{c.name}</span>
                    <span className="block font-body text-xs text-ash">{t(c.role)}</span>
                  </span>
                </Link>
              ); })}
            </div>
          </div>
        )}
      </div>
      <div className="border-t border-edge bg-paper">
        <Discussion entityType="person" entityId={p.id} title="Discussion" />
      </div>
    </div>
  );
}
